import React from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import { useApplicationSettingsContext } from '../contexts/ApplicationSettingsContext';
import { Shield, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PageId } from '../hooks/useApplicationSettings';

interface PermissionGuardProps {
  permission: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  pageId?: PageId; // Optional: check if page is disabled in Application Settings
}

export default function PermissionGuard({ permission, children, fallback, pageId }: PermissionGuardProps) {
  const { hasPermission, loading } = usePermissions();
  const { isPageDisabled } = useApplicationSettingsContext();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Check if page is disabled in Application Settings
  const isDisabled = pageId && isPageDisabled(pageId);
  
  if (!hasPermission(permission) || isDisabled) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <Shield className="mx-auto h-12 w-12 text-gray-400" />
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Access Denied
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              {isDisabled 
                ? "This page has been disabled by your administrator. Please contact them if you need access."
                : "You don't have permission to access this page. Please contact your administrator if you believe this is an error."
              }
            </p>
          </div>
          
          <div className="mt-8">
            <button
              onClick={() => navigate(-1)}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </button>
          </div>
          
          <div className="text-xs text-gray-500">
            {isDisabled 
              ? `Page disabled: ${pageId}`
              : `Missing permission: ${permission}`
            }
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
} 